/* Borderline winter 26/27 · live store stock (R3, 2026-10-21).
   The owner (10/21): the per-store counts come straight from each store's inventory, no synced field. One Storefront
   API request per product per page view, POSTed to the shop's own domain with the PUBLIC access token of the Headless
   storefront (theme setting bl_storefront_token; the section or block that asks prints it as data-bl-sf). The answer
   is kept for the page view: no polling, no retry.
   Anything short of a full answer — no token, a network or HTTP error, GraphQL errors (a token without the inventory
   permission is refused), product: null (a product not on the Headless channel yet) — resolves to null, and the caller
   keeps the words the server rendered.
   Loaded by assets/bl-window.js (ON NOW cards) and assets/bl-pdp.js (Get it) with the URL their Liquid prints
   (data-bl-stock-src), so the asset keeps its versioned URL without an import-map entry. */

const API = '/api/2025-07/graphql.json';
const QUERY = `query BlStock($handle: String!) {
  product(handle: $handle) {
    variants(first: 100) {
      pageInfo { hasNextPage }
      nodes {
        id
        title
        availableForSale
        currentlyNotInStock
        quantityAvailable
        storeAvailability(first: 10) { nodes { available quantityAvailable pickUpTime location { id } } }
      }
    }
  }
}`;

/**
 * @typedef {{ q: number, a: boolean, t: string }} BlStoreCount - q: units available at the store (≥ 0); a: Shopify's
 *   pickup flag; t: its pickup time, lower case ("usually ready in 4 hours"; "… 2-4 days" = Shopify would move the
 *   piece from another store, ship to store: not stock there)
 * @typedef {{ id: string, title: string, sale: boolean, q: number | null, at: Map<string, BlStoreCount> }} BlVariantStock
 *   - q: units for sale across the locations (null when the inventory is not tracked); at: the stores that carry it,
 *   by location id
 * @typedef {{ complete: boolean, variants: Map<string, BlVariantStock> }} BlStock - variants by numeric id, in the
 *   product's order; complete = false when the product has more than 100 variants (the rest are unknown)
 */

/** @type {Map<string, Promise<BlStock | null>>} */
const asked = new Map();

/** @param {unknown} gid */
const tail = (gid) => String(gid || '').split('/').pop() || '';

/** @param {any} json @returns {BlStock | null} */
function read(json) {
  if (!json || json.errors) return null;
  const product = json.data && json.data.product;
  const list = product && product.variants;
  if (!list || !Array.isArray(list.nodes)) return null;
  /** @type {Map<string, BlVariantStock>} */
  const variants = new Map();
  for (const node of list.nodes) {
    if (!node || !node.id) continue;
    /** @type {Map<string, BlStoreCount>} */
    const at = new Map();
    for (const store of (node.storeAvailability && node.storeAvailability.nodes) || []) {
      const loc = tail(store && store.location && store.location.id);
      if (!loc) continue;
      at.set(loc, {
        q: Math.max(0, Number(store.quantityAvailable) || 0),
        a: store.available === true,
        t: String(store.pickUpTime || '').toLowerCase(),
      });
    }
    let q = typeof node.quantityAvailable === 'number' ? node.quantityAvailable : null;
    // not tracked: for sale with nothing counted and not a back-order (the server counts such a size as 1)
    if (q !== null && q <= 0 && node.availableForSale && !node.currentlyNotInStock) q = null;
    variants.set(tail(node.id), { id: tail(node.id), title: String(node.title || ''), sale: node.availableForSale === true, q, at });
  }
  return { complete: !(list.pageInfo && list.pageInfo.hasNextPage), variants };
}

/**
 * The product's stock by variant and store, asked once per page view.
 * @param {string} handle
 * @param {string} token - the Storefront API public access token
 * @returns {Promise<BlStock | null>}
 */
export function productStock(handle, token) {
  if (!handle || !token) return Promise.resolve(null);
  const cached = asked.get(handle);
  if (cached) return cached;
  /** @type {RequestInit} */
  const init = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Shopify-Storefront-Access-Token': token },
    body: JSON.stringify({ query: QUERY, variables: { handle } }),
  };
  if (typeof AbortSignal !== 'undefined' && 'timeout' in AbortSignal) init.signal = AbortSignal.timeout(8000);
  const request = fetch(new URL(API, location.origin), init)
    .then((response) => (response.ok ? response.json() : null))
    .then(read)
    .catch(() => null);
  asked.set(handle, request);
  return request;
}

/* ---------- the shopper's store (R17b, 2026-10-29) ----------
   The header's store chooser (assets/bl-header.js, "the store choice") keeps the shopper's store; modules that load
   this file (the PDP's Get it and stock lines, the home's ON NOW cards) can read it from here instead of repeating
   the storage format. Key = the store's code in lower case ('rh' | 'nm' | 'ot'). */

/**
 * The shopper's store: the header's choice when it is on the page, else the stored one, else null.
 * @returns {{ key: string, name: string, short: string, chosen: boolean } | null}
 */
export function chosenStore() {
  const api = /** @type {any} */ (window).BL?.store;
  if (api && typeof api.get === 'function') {
    const current = api.get();
    if (current) return { key: current.key, name: current.name, short: current.short, chosen: current.chosen === true };
  }
  const key = document.documentElement.getAttribute('data-bl-store');
  try {
    const stored = JSON.parse(localStorage.getItem('bl-store') || 'null');
    if (stored && typeof stored === 'object') {
      const k = String(stored.key || stored.short || '').toLowerCase();
      if (k) return { key: k, name: String(stored.name || ''), short: String(stored.short || k.toUpperCase()), chosen: true };
    }
  } catch (error) {
    /* storage blocked */
  }
  return key ? { key, name: '', short: key.toUpperCase(), chosen: false } : null;
}

/**
 * Calls back with the new store whenever the shopper changes it (here or in another tab).
 * @param {(store: { key: string, name: string, short: string }) => void} callback
 * @param {AbortSignal} [signal]
 */
export function onStoreChange(callback, signal) {
  document.addEventListener(
    'bl:store',
    (event) => {
      const detail = /** @type {CustomEvent} */ (event).detail || {};
      callback({ key: String(detail.key || ''), name: String(detail.name || ''), short: String(detail.short || '') });
    },
    signal ? { signal } : undefined
  );
}
